// ─── La identidad de una factura: su NÚMERO y su documento electrónico ──────
// Una factura de venta se identifica ante la DIAN por prefijo + consecutivo de
// la resolución. Ese número es UNO, aunque se teclee de formas distintas, y no
// se repite nunca: dos documentos con el mismo número son dos facturas para la
// DIAN y una sola para AURA.
//
// Pasó de verdad, y por eso este módulo existe:
//   · La factura de CABLECOL se emitió en el portal de Dataico y se registró a
//     mano en AURA como "MP-157" (con guion, como se venían escribiendo las
//     externas: MP-150, MP-152). El consecutivo de la resolución es "MP157",
//     sin guion.
//   · El control de duplicados comparaba los números LETRA POR LETRA, así que
//     "MP-157" y "MP157" no chocaban, y registrar una factura externa tampoco
//     movía el consecutivo de Dataico.
//   · Minutos después, la factura de CROWN se emitió desde AURA con el
//     consecutivo 157 —que ya estaba usado—. Dataico es idempotente por
//     (prefijo, número): en vez de emitir nada nuevo devolvió el documento que
//     YA existía con ese número (el de CABLECOL, $8.910.720), y AURA guardó ese
//     CUFE, ese QR y ese PDF en la factura de CROWN ($935.295).
// El resultado: una venta sin factura electrónica y una factura apuntando al
// documento de otro cliente.
import { parseNumber } from "./utils.js";

// El número tal como lo identifica la DIAN: sin guiones, espacios ni puntos, y
// en mayúsculas. "MP-157", "mp 157" y "MP157" son el MISMO número.
export function normNumeroFactura(n) {
  return String(n == null ? "" : n).toUpperCase().replace(/[^A-Z0-9]/g, "");
}

// El número partido en su prefijo y su consecutivo, que es lo que permite saber
// si pertenece a la serie de la resolución. Devuelve null si no tiene la forma
// de un consecutivo (una factura de otro sistema puede no tenerla).
export function partesNumeroFactura(n) {
  const m = normNumeroFactura(n).match(/^([A-Z]*)(\d+)$/);
  if (!m) return null;
  return { prefijo: m[1], consecutivo: parseInt(m[2], 10) };
}

// La factura que YA tiene ese número, comparando como lo hace la DIAN. Las
// anuladas cuentan: un consecutivo anulado queda consumido para siempre.
export function facturaConNumero(numero, facturas, exceptoId) {
  const clave = normNumeroFactura(numero);
  if (!clave) return null;
  return (facturas || []).find(f => f && f.id !== exceptoId
    && normNumeroFactura(f.numero) === clave) || null;
}

// El primer consecutivo libre de la serie desde `desde` en adelante: así, al
// chocar, se puede decir con cuál seguir en vez de solo negarse.
export function proximoConsecutivoLibre(prefijo, desde, facturas) {
  const pre = normNumeroFactura(prefijo);
  const usados = new Set((facturas || []).map(f => normNumeroFactura(f && f.numero)));
  let n = Math.max(1, parseInt(desde, 10) || 1);
  while (usados.has(pre + n)) n++;
  return n;
}

// ─── ¿El documento que devolvió Dataico es EL NUESTRO? ──────────────────────
// El QR de la representación gráfica lleva la identidad del documento firmado:
// el NIT del adquirente (DocAdq) y el total (ValTolFac). Si no coinciden con la
// factura que se acaba de mandar, lo que llegó es OTRO documento y su CUFE no
// se puede guardar como si fuera el de esta venta.
export function identidadQRDian(qr) {
  const texto = String(qr || "");
  if (!texto) return null;
  const leer = (campo) => {
    const m = texto.match(new RegExp(campo + "=([^\\n\\r&]*)"));
    return m ? m[1].trim() : "";
  };
  const numero = leer("NumFac");
  const nit = leer("DocAdq").replace(/\D/g, "");
  const total = parseNumber(leer("ValTolFac"));
  if (!numero && !nit && !total) return null;
  return { numero, nit, total: total || 0 };
}

// Solo dice que NO es nuestro cuando hay con qué compararlo y discrepa: una
// respuesta sin QR no puede invalidar una emisión que la DIAN aceptó.
export function documentoAjeno(qr, esperado) {
  const id = identidadQRDian(qr);
  if (!id || !esperado) return null;
  const nitEsperado = String(esperado.nit || "").replace(/\D/g, "");
  const nitDoc = id.nit;
  // El NIT puede venir con o sin dígito de verificación: se compara el número
  // de identificación (sin DV) por el lado más corto.
  const mismoNit = !nitDoc || !nitEsperado
    || nitDoc === nitEsperado
    || nitDoc.startsWith(nitEsperado) || nitEsperado.startsWith(nitDoc);
  const totalEsperado = Math.round(parseNumber(esperado.total) || 0);
  // Un peso de diferencia es redondeo del XML, no otro documento.
  const mismoTotal = !id.total || !totalEsperado
    || Math.abs(Math.round(id.total) - totalEsperado) <= 1;
  if (mismoNit && mismoTotal) return null;
  return { numero: id.numero, nit: nitDoc, total: id.total };
}
